/** Form / API shape for Mutawatteen `SabilHouseholdMember` rows. */
export type SabilHouseholdMemberInput = {
  id?: string;
  name?: string;
  whatsappNo?: string;
  isHof?: boolean;
  itsNo?: string | null;
  sortOrder?: number | null;
};

export type SabilHouseholdMemberForApi = {
  id?: string;
  name: string;
  whatsappNo: string;
  isHof: boolean;
  itsNo: string;
  sortOrder: number;
};

export function isMuttavatteenSabilType(type: string | null | undefined): boolean {
  const t = (type ?? "").trim().toUpperCase();
  return t === "MUTTAVATTEEN" || t === "MUTTAVATTEEN_YEARLY";
}

export const E164_PHONE_REGEX = /^\+[1-9]\d{7,14}$/;

export function isE164PhoneNumber(value: unknown): boolean {
  return typeof value === "string" && E164_PHONE_REGEX.test(value.trim());
}

/** E.164 phone validator with clear user feedback. */
export function validateE164PhoneNumber(
  value: unknown,
  options?: { required?: boolean; fieldLabel?: string }
): string | undefined {
  const label = options?.fieldLabel ?? "Phone number";
  const str = String(value ?? "").trim();
  if (!str) {
    return options?.required ? `${label} is required` : undefined;
  }
  if (!E164_PHONE_REGEX.test(str)) {
    return `Enter ${label.toLowerCase()} in E.164 format with '+' prefix (e.g. +919876543210)`;
  }
  return undefined;
}

/** Normalizes phone string to E.164 format with '+' prefix, max 20 characters. */
export function normalizeWhatsappNo(value: unknown): string {
  const str = String(value ?? "").trim();
  if (!str) {
    return "";
  }

  // 1. Strip all non-digit characters
  let digits = str.replace(/\D/g, "");
  if (!digits) {
    return "";
  }

  // 2. Strip leading 00 (international prefix e.g. 0091... -> 91...)
  if (digits.startsWith("00")) {
    digits = digits.substring(2);
  }

  // 3. Convert 11-digit leading 0 to 91 (Indian 098XXXXXXXX -> 9198XXXXXXXX)
  if (digits.length === 11 && digits.startsWith("0")) {
    digits = `91${digits.substring(1)}`;
  }
  // 4. Convert 10-digit Indian local mobile to 91XXXXXXXXXX
  else if (digits.length === 10) {
    digits = `91${digits}`;
  }

  return `+${digits}`.slice(0, 20);
}

/** User-visible E.164-style label with '+' prefix. */
export function formatDisplayWhatsappNo(value: unknown, options?: { empty?: string }): string {
  const empty = options?.empty ?? "";
  const normalized = normalizeWhatsappNo(value);
  if (!normalized) {
    return empty;
  }
  return normalized;
}

/**
 * Drop blank rows, normalize fields, force isHof false, and assign sortOrder from list order.
 * Returns null when the list is empty after filtering.
 */
export function mapHouseholdMembersForApi(
  members: SabilHouseholdMemberInput[] | null | undefined
): SabilHouseholdMemberForApi[] | null {
  if (!Array.isArray(members)) {
    return null;
  }

  const mapped: SabilHouseholdMemberForApi[] = [];
  for (const [index, raw] of members.entries()) {
    const name = String(raw?.name ?? "").trim();
    const whatsappNo = normalizeWhatsappNo(raw?.whatsappNo);
    const itsNo = String(raw?.itsNo ?? "").trim();
    if (!name && !whatsappNo && !itsNo) {
      continue;
    }
    mapped.push({
      id: raw?.id ? String(raw.id) : undefined,
      name,
      whatsappNo,
      isHof: false,
      itsNo,
      sortOrder: typeof raw?.sortOrder === "number" ? raw.sortOrder : index,
    });
  }

  return mapped.length ? mapped.map((m, i) => ({ ...m, sortOrder: i })) : null;
}

/** Strip leftover holder rows from household members for the additional-family ArrayInput. */
export function additionalMembersOnly(
  members: SabilHouseholdMemberInput[] | null | undefined
): SabilHouseholdMemberInput[] {
  if (!Array.isArray(members)) {
    return [];
  }

  return members
    .filter((member) => member.isHof !== true)
    .map((member) => ({
      ...member,
      isHof: false,
    }));
}

/**
 * Split Mutawatteen holder fields from persisted household rows for edit/show forms.
 * Holder WhatsApp prefers `holderWhatsappNo`, then a leftover isHof row.
 */
export function prepareMuttavatteenEditFormRecord(
  record: Record<string, unknown> | null | undefined
): Record<string, unknown> | null | undefined {
  if (!record?.id || !isMuttavatteenSabilType(record.sabilType as string)) {
    return record;
  }

  const members = Array.isArray(record.householdMembers)
    ? (record.householdMembers as SabilHouseholdMemberInput[])
    : [];
  const holderRow = members.find((member) => member.isHof === true);
  const holderWhatsapp =
    normalizeWhatsappNo(record.holderWhatsappNo) ||
    normalizeWhatsappNo(record.whatsappNo) ||
    normalizeWhatsappNo(holderRow?.whatsappNo);
  const displayWhatsapp = formatDisplayWhatsappNo(holderWhatsapp);

  return {
    ...record,
    householdMembers: additionalMembersOnly(members),
    ...(displayWhatsapp ? { whatsappNo: displayWhatsapp } : {}),
  };
}

/** React-admin form validator: holder WhatsApp required on Mutawatteen sabils in E.164 format. */
export function validateMuttavatteenHolderWhatsapp(
  values: Record<string, unknown>,
  previousData?: Record<string, unknown>
): string | undefined {
  const sabilType = String(values.sabilType ?? previousData?.sabilType ?? "");
  if (!isMuttavatteenSabilType(sabilType)) {
    return undefined;
  }
  return validateE164PhoneNumber(values.whatsappNo, {
    required: true,
    fieldLabel: "Holder WhatsApp",
  });
}

/**
 * React-admin ArrayInput validator for additional family members only.
 * Empty list is valid; each non-blank row needs name and ITS. WhatsApp is optional (validated in E.164 when provided).
 */
export function validateAdditionalHouseholdMembers(
  value: SabilHouseholdMemberInput[] | null | undefined
): string | undefined {
  if (!Array.isArray(value) || value.length === 0) {
    return undefined;
  }

  for (const raw of value) {
    const name = String(raw?.name ?? "").trim();
    const itsNo = String(raw?.itsNo ?? "").trim();
    const whatsappNo = String(raw?.whatsappNo ?? "").trim();

    if (!name && !itsNo && !whatsappNo) {
      continue;
    }

    if (!name) {
      return "Each family member needs a name";
    }
    if (!itsNo) {
      return "Every family member needs an ITS number";
    }
    if (whatsappNo) {
      const phoneError = validateE164PhoneNumber(whatsappNo, {
        required: false,
        fieldLabel: "Family member WhatsApp",
      });
      if (phoneError) {
        return phoneError;
      }
    }
  }

  return undefined;
}
